/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 照片标签埋点封装层 (统一入口, 业务代码不直接调 sensors.track)
 *
 * 规范化事件 + 复用通用属性 (string1/number1/bool1/list1/tabName/labelName/photoNum/fromPage),
 * 仅 toastName 为净新增. 老埋点 (b8084/b8085/b8086 等) 继续保留在原位置, 不在本文件.
 */
import sensors from '../../../utils/sensors'

// =============== 公共类型 ===============

export type FromPage = 'photov2' | 'ai_photo_king' | 'photo'
export type TabName = 'team_label' | 'custom_label'
export type ActionResult = 'success' | 'fail'

// 照片标签入口来源 (引导 / 管理 / 其他) —— 用于 enter 的 elementFrom 维度
export type TagEntryFrom =
  | 'tag_entry_guide' // 限免引导 CTA (teamTagCount===0) —— 归类: 引导
  | 'tag_entry_manage' // smart-row「管理标签」link (teamTagCount>0) —— 归类: 管理
  | 'header_open_manage' // 头部「管理标签」按钮 —— 归类: 管理
  | 'tag_filter_empty_cta' // 标签筛选空态「立即设置标签」 —— 归类: 引导
  | 'other' // 其他 / 未知

const TRACK_SIGN_PREFIX = 'aworkgroup'

// =============== web_picspage 事件 ===============

type PicspageClickItem =
  | 'card_add_btn'
  | 'card_chip_remove'
  | 'card_chip_replace_open'
  | 'enter_batch_mode'
  | 'batch_toggle_check'
  | 'batch_select_all'
  | 'batch_unselect_all'
  | 'batch_cancel'
  | 'batch_apply_label'
  | 'batch_remove_open'
  | 'header_open_manage'
  // v4 新增: 入口 / 筛选 点击
  | 'tag_entry_guide_click' // smart-row 限免引导 CTA 点击
  | 'tag_entry_manage_stats' // smart-row「团队已打标 X 张」统计入口点击
  | 'tag_filter_open' // 「标签筛选」pill 点击, 打开 TagPicker
  | 'tag_filter_empty_cta' // 标签筛选空态「立即设置标签」
  | 'preview_label_add' // 预览页 (ViewerV2 InfoPanel)「+ 添加标签」点击

export interface PicspageClickParams {
  clickItem: PicspageClickItem
  fromPage: FromPage
  bool1?: boolean
  string1?: string
  labelName?: string
  tabName?: TabName
  number1?: number
  enterFrom?: string // 规范 §0.4 来源页面 (v4 起补)
  elementFrom?: string // 规范 §0.4 来源元素 (v4 起补)
}

export function trackPicspageClick(params: PicspageClickParams) {
  sensors.track('workgroup_click_web_picspage', {
    currentPageName: 'web_picspage',
    track_sign: `${TRACK_SIGN_PREFIX}.click.web_picspage`,
    ...params
  })
}

type PicspagePopTitle =
  | 'card_tag_popover'
  | 'card_chip_replace_popover'
  | 'batch_select_labels_add'
  | 'batch_select_labels_remove'
  | 'tag_filter_picker' // v4: 标签筛选 TagPicker 弹窗出现
  // v4 新增: 入口曝光 (复用 dialog_show 承载「UI 面出现」, 见文档 §3.6)
  | 'tag_entry_guide' // 限免引导态曝光 (teamTagCount===0)
  | 'tag_entry_manage' // 已打标管理态曝光 (teamTagCount>0)

export interface PicspageDialogShowParams {
  popTitle: PicspagePopTitle
  fromPage: FromPage
  number1?: number
  enterFrom?: string // 规范 §0.4
  elementFrom?: string // 规范 §0.4
}

export function trackPicspageDialogShow(params: PicspageDialogShowParams) {
  sensors.track('workgroup_dialog_show_web_picspage', {
    currentPageName: 'web_picspage',
    track_sign: `${TRACK_SIGN_PREFIX}.dialog_show.web_picspage`,
    ...params
  })
}

type PicspageDialogClickItem =
  | 'select_tag'
  | 'search'
  | 'confirm'
  | 'cancel'
  | 'close'
  | 'open_manage'

export interface PicspageDialogClickParams {
  popTitle: PicspagePopTitle
  clickItem: PicspageDialogClickItem
  fromPage: FromPage
  string1?: string
  labelName?: string
  tabName?: TabName
}

export function trackPicspageDialogClick(params: PicspageDialogClickParams) {
  sensors.track('workgroup_dialog_click_web_picspage', {
    currentPageName: 'web_picspage',
    track_sign: `${TRACK_SIGN_PREFIX}.dialog_click.web_picspage`,
    ...params
  })
}

type PicspageToastName =
  | 'tag_remove_not_supported'
  | 'single_bind_success'
  | 'single_bind_fail'
  | 'single_unbind_success'
  | 'single_unbind_fail'
  | 'single_replace_success'
  | 'single_replace_fail'
  | 'batch_bind_success'
  | 'batch_bind_fail'
  | 'batch_unbind_success'
  | 'batch_unbind_fail'
  | 'batch_no_selection'
  | 'batch_no_label_selected'

export interface PicspageToastParams {
  toastName: PicspageToastName
  fromPage: FromPage
}

export function trackPicspageToast(params: PicspageToastParams) {
  sensors.track('workgroup_toast_show_web_picspage', {
    currentPageName: 'web_picspage',
    track_sign: `${TRACK_SIGN_PREFIX}.toast_show.web_picspage`,
    ...params
  })
}

type PicspageActionName =
  | 'bind_label_single'
  | 'bind_label_single_exclusive'
  | 'bind_label_batch'
  | 'unbind_label_single'
  | 'unbind_label_batch'

export interface PicspageResultParams {
  actionName: PicspageActionName
  actionResult: ActionResult
  costTime: number
  errorMsg?: string
  photoNum: number
  number1: number // 标签个数
  list1: string[] // 标签名列表
  tabName: TabName | 'mixed'
  bool1?: boolean // exclusive 标识
  fromPage: FromPage
}

export function trackPicspageResult(params: PicspageResultParams) {
  sensors.track('workgroup_result_web_picspage', {
    currentPageName: 'web_picspage',
    track_sign: `${TRACK_SIGN_PREFIX}.result.web_picspage`,
    ...params
  })
}

// =============== web_photo_label_manage 事件 ===============

export interface PhotoLabelManageEnterParams {
  tabName: TabName
  number1: number // 当前 tab 下可用标签数
  elementFrom: TagEntryFrom // v4: 来源入口 (引导/管理/其他), 豪远 needs #2
  enterFrom?: string // 来源页面, 默认 web_picspage
}

export function trackPhotoLabelManageEnter(
  params: PhotoLabelManageEnterParams
) {
  sensors.track('workgroup_enter_web_photo_label_manage', {
    currentPageName: 'web_photo_label_manage',
    track_sign: `${TRACK_SIGN_PREFIX}.enter.web_photo_label_manage`,
    enterFrom: 'web_picspage',
    ...params
  })
}

type ManageClickItem =
  | 'switch_tab'
  | 'add_label'
  | 'confirm_add_label'
  | 'cancel_add_label'
  | 'edit_label'
  | 'save_edit_label'
  | 'cancel_edit_label'
  | 'delete_label'
  | 'close'
  // v4 新增: 标签开关
  | 'toggle_disable' // 停用标签
  | 'toggle_enable' // 启用标签
  | 'toggle_force_label' // 全员必填 (forceLabel) 开关
  | 'disable_feature' // 关闭整个照片标签功能 (团队级一键关闭)

export interface PhotoLabelManageClickParams {
  clickItem: ManageClickItem
  tabName: TabName
  string1?: string
  labelName?: string
  bool1?: boolean // v4: 开关类 toggle 的目标状态 (停用/必填 = true/false)
}

export function trackPhotoLabelManageClick(
  params: PhotoLabelManageClickParams
) {
  sensors.track('workgroup_click_web_photo_label_manage', {
    currentPageName: 'web_photo_label_manage',
    track_sign: `${TRACK_SIGN_PREFIX}.click.web_photo_label_manage`,
    ...params
  })
}

type ManageToastName =
  | 'create_label_success'
  | 'create_label_fail'
  | 'update_label_success'
  | 'update_label_fail'
  | 'delete_label_success'
  | 'delete_label_fail'
  | 'label_name_duplicate'
  | 'label_name_empty'
  | 'label_name_too_long'
  | 'label_count_limit'

export interface PhotoLabelManageToastParams {
  toastName: ManageToastName
  tabName?: TabName // 仅 success/fail/limit 类带
}

export function trackPhotoLabelManageToast(
  params: PhotoLabelManageToastParams
) {
  sensors.track('workgroup_toast_show_web_photo_label_manage', {
    currentPageName: 'web_photo_label_manage',
    track_sign: `${TRACK_SIGN_PREFIX}.toast_show.web_photo_label_manage`,
    ...params
  })
}

type ManageActionName =
  | 'create_label'
  | 'update_label'
  | 'delete_label'
  | 'disable_feature' // 关闭整个照片标签功能

export interface PhotoLabelManageResultParams {
  actionName: ManageActionName
  actionResult: ActionResult
  costTime: number
  errorMsg?: string
  string1?: string // labelID, create 时可空
  labelName: string
  tabName: TabName
}

export function trackPhotoLabelManageResult(
  params: PhotoLabelManageResultParams
) {
  sensors.track('workgroup_result_web_photo_label_manage', {
    currentPageName: 'web_photo_label_manage',
    track_sign: `${TRACK_SIGN_PREFIX}.result.web_photo_label_manage`,
    ...params
  })
}

// =============== web_photo_label_stats 事件 (v4 新增: 团队打标统计弹窗) ===============

export type StatsDataState = 'normal' | 'empty' | 'error'

export interface PhotoLabelStatsEnterParams {
  number1?: number // 近 7 天已打标数
  number2?: number // 近 7 天未打标数
  number3?: number // 近 7 天拍照数
  string1?: StatsDataState // 数据状态
  fromPage?: FromPage
  enterFrom?: string // 默认 web_picspage
  elementFrom?: string // 默认 tag_entry_manage_stats
}

export function trackPhotoLabelStatsEnter(params: PhotoLabelStatsEnterParams) {
  sensors.track('workgroup_enter_web_photo_label_stats', {
    currentPageName: 'web_photo_label_stats',
    track_sign: `${TRACK_SIGN_PREFIX}.enter.web_photo_label_stats`,
    enterFrom: 'web_picspage',
    elementFrom: 'tag_entry_manage_stats',
    ...params
  })
}

type StatsClickItem = 'close' // 一期统计弹窗只有关闭交互;成员行/重试/导出 暂无 UI

export interface PhotoLabelStatsClickParams {
  clickItem: StatsClickItem
}

export function trackPhotoLabelStatsClick(params: PhotoLabelStatsClickParams) {
  sensors.track('workgroup_click_web_photo_label_stats', {
    currentPageName: 'web_photo_label_stats',
    track_sign: `${TRACK_SIGN_PREFIX}.click.web_photo_label_stats`,
    enterFrom: 'web_picspage',
    ...params
  })
}

// =============== web_photo_label_feedback 事件（纯埋点收集，无后端） ===============
// 复用照片标签现有 enter / result 体系；groupID / userID / role 等是 SDK 公参，不在此重复声明。

export type FeedbackSource = 'manage' | 'list' | 'stats'

export interface PhotoLabelFeedbackEnterParams {
  /** 入口元素：manage(标签管理弹窗) / list(照片列表头部) / stats(统计弹窗) */
  elementFrom: FeedbackSource
  /** 来源页面，默认 web_picspage */
  enterFrom?: string
}

/** 打开反馈抽屉（漏斗第一步：点了反馈入口） */
export function trackPhotoLabelFeedbackEnter(
  params: PhotoLabelFeedbackEnterParams
) {
  sensors.track('workgroup_enter_web_photo_label_feedback', {
    currentPageName: 'web_photo_label_feedback',
    track_sign: `${TRACK_SIGN_PREFIX}.enter.web_photo_label_feedback`,
    enterFrom: 'web_picspage',
    ...params
  })
}

export interface PhotoLabelFeedbackSubmitParams {
  /** 入口元素：manage / list / stats */
  elementFrom: FeedbackSource
  /** 问题类型 */
  feedbackType: string
  /** 反馈内容（复用标准 content 属性，神策 STRING，过长会被截断） */
  content: string
  /** 用户填写的联系方式（选填） */
  contact: string
  /** 来源页面，默认 web_picspage */
  enterFrom?: string
}

/**
 * 提交反馈（漏斗第二步：内容随该事件上报）。enter 数 − result 数 = 放弃数。
 * 通用槽对照：string1 = 问题类型(feedbackType)，string2 = 联系方式(contact)；content 复用标准属性。
 */
export function trackPhotoLabelFeedbackSubmit(
  params: PhotoLabelFeedbackSubmitParams
) {
  const { elementFrom, feedbackType, content, contact, enterFrom } = params
  sensors.track('workgroup_result_web_photo_label_feedback', {
    currentPageName: 'web_photo_label_feedback',
    track_sign: `${TRACK_SIGN_PREFIX}.result.web_photo_label_feedback`,
    enterFrom: enterFrom || 'web_picspage',
    actionName: 'submit_feedback',
    actionResult: 'success',
    elementFrom,
    content,
    string1: feedbackType,
    string2: contact
  })
}
